import {
  AVATAR_COLORS,
  AVATAR_EMBLEMS,
  AVATAR_FRAMES,
  type Avatar as AvatarData,
} from "@coup/shared";
import { useState } from "react";
import { COLOR_HEX, EMBLEM_PATH, FRAME_HEX, ICONS, label } from "../art";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import styles from "./AvatarBuilder.module.css";
import ui from "./ui.module.css";

export type Preset = Extract<AvatarData, { kind: "preset" }>;

const pick = <T,>(items: readonly T[]) => items[Math.floor(Math.random() * items.length)]!;

export const randomPreset = (): Preset => ({
  kind: "preset",
  color: pick(AVATAR_COLORS),
  emblem: pick(AVATAR_EMBLEMS),
  frame: pick(AVATAR_FRAMES),
});

const MAX_UPLOAD = 2 * 1024 * 1024;

/** Crops to a centred square and downscales to a 256 px WebP data URL. */
async function toDataUrl(file: File): Promise<string> {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Use a PNG, JPEG or WebP image");
  }
  if (file.size > MAX_UPLOAD) throw new Error("Images must be 2 MB or smaller");
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  canvas.getContext("2d")!.drawImage(bitmap, sx, sy, side, side, 0, 0, 256, 256);
  const webp = canvas.toDataURL("image/webp", 0.85);
  // Browsers that can't encode WebP fall back to PNG, so use JPEG to stay small.
  return webp.startsWith("data:image/webp") ? webp : canvas.toDataURL("image/jpeg", 0.85);
}

interface Props {
  name: string;
  avatar: AvatarData;
  /** Last preset, restored when picking parts after an upload. */
  preset: Preset;
  onName: (name: string) => void;
  onAvatar: (avatar: AvatarData, preset: Preset) => void;
}

export function AvatarBuilder({ name, avatar, preset, onName, onAvatar }: Props) {
  const [uploadError, setUploadError] = useState<string>();
  const setPreset = (p: Preset) => onAvatar(p, p);
  const setFrame = (frame: Preset["frame"]) =>
    avatar.kind === "image"
      ? onAvatar({ ...avatar, frame }, { ...preset, frame })
      : setPreset({ ...preset, frame });

  const upload = async (file?: File) => {
    if (!file) return;
    try {
      setUploadError(undefined);
      onAvatar({ kind: "image", dataUrl: await toDataUrl(file), frame: avatar.frame }, preset);
    } catch (e) {
      setUploadError((e as Error).message);
    }
  };

  return (
    <div className={styles.builder}>
      <div className={styles.left}>
        <Avatar avatar={avatar} size={128} ring={5} className={styles.preview} />
        <button type="button" className={ui.ghost} onClick={() => setPreset(randomPreset())}>
          <Icon path={ICONS.shuffle} size={16} />
          Randomise
        </button>
        <label className={styles.upload}>
          <Icon path={ICONS.upload} size={16} />
          Upload image
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              void upload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <div className={uploadError ? styles.uploadError : styles.hint}>
          {uploadError ?? "Up to 2 MB. This lobby only."}
        </div>
      </div>
      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={ui.label}>Name for this game</span>
          <input
            className={ui.input}
            maxLength={16}
            value={name}
            onChange={(e) => onName(e.target.value)}
          />
        </label>
        <div className={styles.field}>
          <span className={ui.label}>Colour</span>
          <div className={styles.row}>
            {AVATAR_COLORS.map((c) => {
              const on = avatar.kind === "preset" && avatar.color === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={label(c)}
                  className={styles.swatch}
                  style={{
                    background: COLOR_HEX[c],
                    border: on ? "3px solid var(--text)" : "2px solid var(--walnut)",
                  }}
                  onClick={() => setPreset({ ...preset, color: c })}
                />
              );
            })}
          </div>
        </div>
        <div className={styles.field}>
          <span className={ui.label}>Emblem</span>
          <div className={styles.row}>
            {AVATAR_EMBLEMS.map((e) => {
              const on = avatar.kind === "preset" && avatar.emblem === e;
              return (
                <button
                  key={e}
                  type="button"
                  aria-label={label(e)}
                  className={styles.emblem}
                  style={{
                    background: on ? "var(--line)" : "var(--walnut)",
                    border: on ? "2px solid var(--gold)" : "1px solid var(--line)",
                  }}
                  onClick={() => setPreset({ ...preset, emblem: e })}
                >
                  <Icon path={EMBLEM_PATH[e]} size={22} color="#efe6d2" width={1.8} />
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.field}>
          <span className={ui.label}>Frame</span>
          <div className={styles.row}>
            {AVATAR_FRAMES.map((f) => (
              <button
                key={f}
                type="button"
                className={styles.frame}
                style={{
                  borderColor: FRAME_HEX[f],
                  background: avatar.frame === f ? "var(--line)" : "transparent",
                }}
                onClick={() => setFrame(f)}
              >
                {label(f)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
